import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Sparkles } from "lucide-react";
import { getPaymentStatus } from "../lib/boostApi";

const POLL_INTERVAL = 2000;
const MAX_POLLS = 20;

// Handles both /payment/success and /payment/cancel. On success it polls the
// backend until the payment is verified server-side and the boost is activated.
export default function BoostResult({ mode }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(mode === "cancel" ? "cancelled" : "checking");
  const pollsRef = useRef(0);

  useEffect(() => {
    if (mode === "cancel") return;
    if (!sessionId) {
      setState("error");
      return;
    }
    let active = true;
    const tick = async () => {
      if (!active) return;
      pollsRef.current += 1;
      try {
        const data = await getPaymentStatus(sessionId);
        if (!active) return;
        if (data.payment_status === "paid" && data.boost_activated) {
          setState("boosted");
          return;
        }
        if (data.payment_status === "paid") {
          // Paid but activation still settling — keep polling briefly.
          setState("activating");
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch {
        /* transient — keep polling */
      }
      if (pollsRef.current >= MAX_POLLS) {
        setState((s) => (s === "boosted" ? s : "timeout"));
        return;
      }
      setTimeout(tick, POLL_INTERVAL);
    };
    tick();
    return () => {
      active = false;
    };
  }, [mode, sessionId]);

  const busy = state === "checking" || state === "activating";

  return (
    <div
      data-testid="boost-result-page"
      className="flex min-h-[100dvh] flex-col items-center justify-center bg-[#050505] px-6 text-center"
    >
      <div className="w-full max-w-md">
        {busy && (
          <div data-testid="boost-result-loading" className="flex flex-col items-center">
            <Loader2 className="h-10 w-10 animate-spin text-[#ff2a00]" />
            <h1 className="mt-6 font-display text-2xl font-extrabold text-white">
              Confirming your payment…
            </h1>
            <p className="mt-2 text-sm text-white/50">
              Please don't close this page. This only takes a moment.
            </p>
          </div>
        )}

        {state === "boosted" && (
          <div data-testid="boost-result-success" className="flex flex-col items-center">
            <div className="relative">
              <CheckCircle2 className="h-14 w-14 text-[#4ade80]" />
              <Sparkles className="absolute -right-2 -top-2 h-5 w-5 text-[#ff2a00]" />
            </div>
            <h1 className="mt-6 font-display text-2xl font-extrabold text-white">
              Your event is boosted!
            </h1>
            <p className="mt-2 text-sm text-white/60">
              It's now Featured for 7 days and highlighted on the map.
            </p>
            <div className="mt-8 flex w-full flex-col gap-3">
              <button
                data-testid="boost-result-view-events"
                onClick={() => navigate("/my-events")}
                className="w-full rounded-full bg-[#ff2a00] py-3.5 text-base font-bold text-white transition-transform duration-200 active:scale-[0.98]"
              >
                Back to my events
              </button>
              <button
                data-testid="boost-result-view-discover"
                onClick={() => navigate("/")}
                className="w-full rounded-full border border-white/15 py-3.5 text-base font-semibold text-white transition-colors hover:bg-white/5"
              >
                See it on Discover
              </button>
            </div>
          </div>
        )}

        {state === "cancelled" && (
          <div data-testid="boost-result-cancelled" className="flex flex-col items-center">
            <XCircle className="h-14 w-14 text-white/40" />
            <h1 className="mt-6 font-display text-2xl font-extrabold text-white">
              Checkout cancelled
            </h1>
            <p className="mt-2 text-sm text-white/60">
              No payment was taken. Your event has not been boosted.
            </p>
            <button
              data-testid="boost-result-back"
              onClick={() => navigate("/my-events")}
              className="mt-8 w-full rounded-full bg-[#ff2a00] py-3.5 text-base font-bold text-white transition-transform duration-200 active:scale-[0.98]"
            >
              Back to my events
            </button>
          </div>
        )}

        {["timeout", "failed", "error"].includes(state) && (
          <div data-testid="boost-result-error" className="flex flex-col items-center">
            <XCircle className="h-14 w-14 text-[#ff6a4d]" />
            <h1 className="mt-6 font-display text-2xl font-extrabold text-white">
              {state === "failed" ? "Payment not completed" : "Still confirming…"}
            </h1>
            <p className="mt-2 text-sm text-white/60">
              {state === "failed"
                ? "Your event was not boosted. You can try again from My Events."
                : "If you completed payment, your boost will appear shortly in My Events."}
            </p>
            <button
              data-testid="boost-result-back"
              onClick={() => navigate("/my-events")}
              className="mt-8 w-full rounded-full bg-[#ff2a00] py-3.5 text-base font-bold text-white transition-transform duration-200 active:scale-[0.98]"
            >
              Back to my events
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
